import { DateTime } from 'luxon';

import {
  formatHeightForUnit,
  formatWeightForUnit,
  heightRecordToInches,
  weightRecordToLbs
} from 'utils/units';

interface IMeasurementPages {
  measurements?: Record<string, IMeasurementClass>;
}

export interface IBodyMeasurementFields {
  height?: string;
  heightInches?: string;
  weight?: string;
}

const toMillis = (date: string): number => {
  const sql = DateTime.fromSQL(date);
  const parsed = sql.isValid ? sql : DateTime.fromISO(date);
  return parsed.isValid ? parsed.toMillis() : 0;
};

const collectRecords = (data: IMeasurementPages): IMeasurementRecord[] =>
  Object.values(data?.measurements ?? {}).flatMap((group) => group?.measurements ?? []);

const latestRecord = (
  records: IMeasurementRecord[],
  className: string
): IMeasurementRecord | undefined =>
  records
    .filter((record) => record.class === className)
    .sort((a, b) => toMillis(b.measure_date) - toMillis(a.measure_date))[0];

const readRecord = (record: IMeasurementRecord): { value: number; unit: string } | null => {
  const hasDisplay = record.display_unit && record.display_value !== undefined;
  const value = parseFloat(String(hasDisplay ? record.display_value : record.value));
  const unit = hasDisplay ? record.display_unit : record.unit;
  return Number.isFinite(value) && value > 0 && unit ? { value, unit } : null;
};

/** The patient's latest height and weight, expressed in the given (preferred) units. */
export const getLatestBodyMeasurements = (
  data: IMeasurementPages,
  heightUnit: string,
  weightUnit: string
): IBodyMeasurementFields => {
  const records = collectRecords(data);
  const result: IBodyMeasurementFields = {};

  const heightRecord = latestRecord(records, 'height');
  const height = heightRecord && readRecord(heightRecord);
  const inches = height && heightRecordToInches(height.value, height.unit);
  if (inches) Object.assign(result, formatHeightForUnit(inches, heightUnit));

  const weightRecord = latestRecord(records, 'weight');
  const weight = weightRecord && readRecord(weightRecord);
  const lbs = weight && weightRecordToLbs(weight.value, weight.unit);
  if (lbs) result.weight = formatWeightForUnit(lbs, weightUnit);

  return result;
};
